import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEdit2, FiTrash2, FiPlus, FiX, FiChevronDown, FiChevronUp, FiLayers, FiBookOpen } = FiIcons;

// Colors for consistency with other views
const CLUSTER_COLORS = [
  '#5470c6', '#91cc75', '#fac858', '#ee6666', 
  '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc'
];

const ManageQuestions = () => {
  const [questions, setQuestions] = useState([]);
  const [elements, setElements] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [elementClusters, setElementClusters] = useState([]);
  const [recommendationCounts, setRecommendationCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [formData, setFormData] = useState({ text: '', element_id: '' });
  const [expandedElements, setExpandedElements] = useState({});

  // Tab State
  const [activeClusterId, setActiveClusterId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [questionsRes, elementsRes, clustersRes, relationsRes, recRelationsRes] = await Promise.all([
        apiService.getQuestions(),
        apiService.getElements(),
        apiService.getClusters(),
        apiService.getClusterElementRelations(),
        apiService.getQuestionRecommendationRelations()
      ]);
      
      if (questionsRes.status === 'success') setQuestions(questionsRes.data);
      if (elementsRes.status === 'success') setElements(elementsRes.data);
      
      let fetchedClusters = [];
      if (clustersRes.status === 'success') {
         fetchedClusters = clustersRes.data;
         setClusters(fetchedClusters);
      }
      
      if (relationsRes.status === 'success') setElementClusters(relationsRes.data);

      if (recRelationsRes.status === 'success' && Array.isArray(recRelationsRes.data)) {
        const counts = {};
        recRelationsRes.data.forEach(rel => {
          const qId = rel.question_id;
          counts[qId] = (counts[qId] || 0) + 1;
        });
        setRecommendationCounts(counts);
      } else {
        setRecommendationCounts({});
      }

      // Initialize active cluster
      if (activeClusterId === null && fetchedClusters.length > 0) {
        setActiveClusterId(fetchedClusters[0].id);
      } else if (activeClusterId === null) {
        setActiveClusterId('none');
      }

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleElement = (elementId) => {
    setExpandedElements(prev => ({
      ...prev,
      [elementId]: !prev[elementId]
    }));
  };

  // Group elements by cluster
  const getClusteredData = () => {
    const grouped = {};
    const noClusterElements = [];

    // Initialize groups for all clusters
    clusters.forEach((c, index) => {
      grouped[c.id] = { 
        ...c, 
        elements: [],
        color: CLUSTER_COLORS[index % CLUSTER_COLORS.length]
      };
    });

    elements.forEach(element => {
      const relation = elementClusters.find(r => r.element_id === element.id);
      const elementQuestions = questions.filter(q => q.element_id === element.id);
      
      const elementWithQuestions = {
        ...element,
        questions: elementQuestions
      };

      if (relation && grouped[relation.cluster_id]) {
        grouped[relation.cluster_id].elements.push(elementWithQuestions);
      } else {
        noClusterElements.push(elementWithQuestions);
      }
    });

    const result = Object.values(grouped); 
    
    // Always add "Nicht zugeordnet" tab
    result.push({
        id: 'none',
        title: 'Nicht zugeordnet',
        description: 'Elemente ohne Cluster-Zuordnung',
        elements: noClusterElements,
        color: '#6b7280'
    });
    
    return result;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        text: formData.text,
        element_id: parseInt(formData.element_id)
      };

      if (currentQuestion) {
        await apiService.updateQuestion(currentQuestion.id, payload);
      } else {
        await apiService.createQuestion(payload);
      }
      
      // Refresh questions only, keeping other data intact is fine, or refresh all
      // Here just refreshing questions is enough, but to be safe and simple calling fetchData or just getQuestions
      const res = await apiService.getQuestions();
      if (res.status === 'success') {
        setQuestions(res.data);
        if (!currentQuestion && payload.element_id) {
          setExpandedElements(prev => ({ ...prev, [payload.element_id]: true }));
        }
      }
      
      closeModal();
    } catch (error) {
      console.error('Error saving question:', error);
      alert('Fehler beim Speichern der Frage');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Möchten Sie diese Frage wirklich löschen?')) {
      try {
        await apiService.deleteQuestion(id);
        const res = await apiService.getQuestions();
        if (res.status === 'success') setQuestions(res.data);
      } catch (error) {
        console.error('Error deleting question:', error);
        alert('Fehler beim Löschen der Frage');
      }
    }
  };

  const openModal = (question = null, preselectedElementId = '') => {
    if (question) {
      setCurrentQuestion(question);
      setFormData({ text: question.text, element_id: question.element_id });
    } else {
      setCurrentQuestion(null);
      // Try to find a sensible default element based on the active cluster
      let defaultElementId = preselectedElementId;
      
      if (!defaultElementId && activeClusterId) {
          const activeClusterData = getClusteredData().find(c => c.id === activeClusterId);
          if (activeClusterData && activeClusterData.elements.length > 0) {
              defaultElementId = activeClusterData.elements[0].id;
          }
      }

      setFormData({ 
        text: '', 
        element_id: defaultElementId || (elements.length > 0 ? elements[0].id : '') 
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentQuestion(null);
    setFormData({ text: '', element_id: '' });
  };

  if (loading) return (
    <div className="flex justify-center items-center h-64">
       <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
    </div>
  );

  const clusteredData = getClusteredData();

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-brand-dark">Fragen verwalten</h2>
        <button
          onClick={() => openModal()}
          disabled={elements.length === 0}
          className={`flex items-center px-4 py-2 rounded-md text-white transition-colors ${
            elements.length === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-brand-secondary hover:bg-brand-dark'
          }`}
        >
          <SafeIcon icon={FiPlus} className="mr-2" /> Neue Frage
        </button>
      </div>

      {elements.length === 0 && (
        <div className="mb-4 p-4 bg-yellow-50 text-yellow-700 rounded-md border border-yellow-200">
          Bitte erstellen Sie zuerst Elemente, bevor Sie Fragen hinzufügen.
        </div>
      )}

      <div className="flex flex-col gap-6">
        {/* Tab Navigation */}
        <div className="flex space-x-2 overflow-x-auto pb-2 scrollbar-hide">
             {clusteredData.map(cluster => (
                <button
                   key={cluster.id}
                   onClick={() => setActiveClusterId(cluster.id)}
                   className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap border ${
                      activeClusterId === cluster.id
                        ? 'bg-[#152627] text-white border-[#152627] shadow-md'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                   }`}
                >
                   {cluster.title}
                </button>
             ))}
        </div>

        {/* Active Cluster Content */}
        {clusteredData.map((cluster) => {
           if (cluster.id !== activeClusterId) return null;

           return (
             <div 
               key={cluster.id} 
               className="rounded-lg border overflow-hidden animate-fadeIn"
               style={{
                  backgroundColor: cluster.id === 'none' ? '#f9fafb' : `${cluster.color}08`, 
                  borderColor: cluster.id === 'none' ? '#e5e7eb' : `${cluster.color}30`
               }}
             >
               <div 
                 className="px-4 py-3 border-b flex justify-between items-center"
                 style={{ 
                   borderColor: cluster.id === 'none' ? '#e5e7eb' : `${cluster.color}30`,
                   backgroundColor: cluster.id === 'none' ? '#f3f4f6' : `${cluster.color}15`
                 }}
               >
                  <div className="flex items-center gap-2">
                     <SafeIcon icon={FiLayers} className="w-5 h-5" style={{ color: cluster.id === 'none' ? '#374151' : cluster.color }} />
                     <h3 className="font-bold text-lg" style={{ color: cluster.id === 'none' ? '#374151' : cluster.color }}>
                        {cluster.title}
                     </h3>
                  </div>
                  <span className="text-xs font-medium px-2 py-1 rounded bg-white bg-opacity-50 text-gray-600">
                     {cluster.elements.length} Elemente
                  </span>
               </div>

               <div className="p-4 space-y-3">
                  {cluster.elements.length === 0 ? (
                    <div className="text-center text-gray-400 italic py-12">
                      Keine Elemente in diesem Cluster
                    </div>
                  ) : (
                    cluster.elements.map((element) => {
                      const isExpanded = expandedElements[element.id];
                      return (
                        <div key={element.id} className="bg-white shadow-sm sm:rounded-md overflow-hidden border border-gray-100">
                          <button
                            onClick={() => toggleElement(element.id)}
                            className="w-full px-6 py-4 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors focus:outline-none"
                          >
                            <div className="flex items-center">
                              <h3 className="text-lg font-medium text-brand-dark mr-3">{element.title}</h3>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand-primary/10 text-brand-secondary">
                                {element.questions.length} {element.questions.length === 1 ? 'Frage' : 'Fragen'}
                              </span>
                            </div>
                            <SafeIcon 
                              icon={isExpanded ? FiChevronUp : FiChevronDown} 
                              className="w-5 h-5 text-gray-500"
                            />
                          </button>

                          {isExpanded && (
                            <div className="border-t border-gray-100 bg-gray-50/50">
                              {element.questions.length > 0 ? (
                                <ul className="divide-y divide-gray-100">
                                  {element.questions.map((question) => {
                                    const recCount = recommendationCounts[question.id] || 0;
                                    return (
                                      <li key={question.id} className="px-6 py-3 flex items-center justify-between hover:bg-white pl-10">
                                        <div className="flex items-start flex-1 mr-4">
                                           <span className="text-gray-400 mr-2 mt-1">•</span>
                                           <div className="flex flex-col">
                                              <h3 className="text-md text-gray-700">{question.text}</h3>
                                              <span className={`inline-flex items-center mt-1 w-fit px-2 py-0.5 rounded-full text-xs font-medium ${recCount > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-400'}`} title={`${recCount} verknüpfte Empfehlungen`}>
                                                 <SafeIcon icon={FiBookOpen} className="w-3 h-3 mr-1" />
                                                 {recCount} {recCount === 1 ? 'Empfehlung' : 'Empfehlungen'}
                                              </span>
                                           </div>
                                        </div>
                                        <div className="flex space-x-2 ml-4 self-start">
                                          <button
                                            onClick={() => openModal(question)}
                                            className="text-brand-secondary hover:text-brand-dark p-1.5 rounded-full hover:bg-brand-secondary/10"
                                            title="Bearbeiten"
                                          >
                                            <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                                          </button>
                                          <button
                                            onClick={() => handleDelete(question.id)}
                                            className="text-red-600 hover:text-red-900 p-1.5 rounded-full hover:bg-red-50"
                                            title="Löschen"
                                          >
                                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </li>
                                    );
                                  })}
                                </ul>
                              ) : (
                                <div className="px-6 py-6 text-center text-gray-400 italic text-sm">
                                  Keine Fragen in diesem Element vorhanden.
                                  <br/>
                                  <button 
                                    onClick={() => openModal(null, element.id)}
                                    className="mt-2 text-brand-secondary hover:underline text-xs"
                                  >
                                    Jetzt erste Frage hinzufügen
                                  </button>
                                </div>
                              )}
                              <div className="px-6 py-2 bg-gray-50 border-t border-gray-100 flex justify-end">
                                 <button 
                                     onClick={() => openModal(null, element.id)}
                                     className="text-xs font-medium text-brand-secondary hover:text-brand-dark flex items-center"
                                 >
                                     <SafeIcon icon={FiPlus} className="mr-1" /> Frage hinzufügen
                                 </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
               </div>
             </div>
           );
        })}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-medium">
                {currentQuestion ? 'Frage bearbeiten' : 'Neue Frage'}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-500">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Zugehöriges Element</label>
                <select
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-primary"
                  value={formData.element_id}
                  onChange={(e) => setFormData({ ...formData, element_id: e.target.value })}
                >
                  {/* Group options in select as well for better UX */}
                  {clusteredData.map(cluster => (
                    <optgroup key={cluster.id} label={cluster.title} style={{ color: cluster.id !== 'none' ? cluster.color : '' }}>
                      {cluster.elements.map(el => (
                        <option key={el.id} value={el.id}>{el.title}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Fragetext</label>
                <textarea
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-primary"
                  rows="3"
                  value={formData.text}
                  onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                />
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-secondary text-white rounded-md hover:bg-brand-dark"
                >
                  Speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageQuestions;